import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { pulseFetch } from '../../pulse/pulseClient';
import { useAuth } from '../../auth/AuthProvider';

/**
 * #260 Wissensdatenbank-Kuration (errorDB + LogDB) + Kandidaten-Inbox.
 * Läuft über den Pulse-BFF-Proxy (`/proxy/pulse`) mit durchgereichtem
 * Operator-Bearer. Pulse verlangt auf den Admin-Routen zusätzlich eine
 * interne Rolle (requireOperator) — Kunden-Tokens werden abgewiesen.
 *
 * Endpoints:
 *   GET    /v1/errors/admin/all?kind=error|log
 *   POST   /v1/errors                                (create/upsert)
 *   PUT    /v1/errors/:id
 *   DELETE /v1/errors/:id
 *   GET    /v1/errors/admin/candidates
 *   POST   /v1/errors/admin/candidates/:id/promote
 *   POST   /v1/errors/admin/candidates/:id/dismiss
 */
function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useKnowledgeEntries(kind = 'error') {
  const auth = useAuth();
  return useQuery({
    queryKey: ['kb-entries', kind],
    queryFn: () =>
      pulseFetch(`/v1/errors/admin/all?kind=${encodeURIComponent(kind)}`, {
        headers: authHeader(auth.getAccessToken()),
      }),
  });
}

export function useKnowledgeCandidates(limit = 200) {
  const auth = useAuth();
  return useQuery({
    queryKey: ['kb-candidates', limit],
    queryFn: () =>
      pulseFetch(`/v1/errors/admin/candidates?limit=${limit}`, {
        headers: authHeader(auth.getAccessToken()),
      }),
    refetchInterval: 60_000,
  });
}

export function useSaveEntry() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, entry) => {
      const isUpdate = !!entry.id;
      return pulseFetch(isUpdate ? `/v1/errors/${entry.id}` : '/v1/errors', {
        method: isUpdate ? 'PUT' : 'POST',
        headers: { 'content-type': 'application/json', ...authHeader(auth.getAccessToken()) },
        body: entry,
      });
    },
    fixture: (entry) => ({ error: { ...entry, id: entry.id ?? 999 } }),
    invalidate: ['kb-entries'],
    successMessage: (_, entry) => (entry.id ? 'Eintrag aktualisiert.' : 'Eintrag angelegt.'),
  });
}

export function useDeleteEntry() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id }) =>
      pulseFetch(`/v1/errors/${id}`, { method: 'DELETE', headers: authHeader(auth.getAccessToken()) }),
    fixture: () => ({ ok: true }),
    invalidate: ['kb-entries'],
    successMessage: () => 'Eintrag gelöscht.',
  });
}

export function usePromoteCandidate() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id, ...body }) =>
      pulseFetch(`/v1/errors/admin/candidates/${id}/promote`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeader(auth.getAccessToken()) },
        body,
      }),
    fixture: ({ id }) => ({ error: { id } }),
    invalidate: ['kb-candidates', 'kb-entries'],
    successMessage: () => 'Kandidat in die Wissensdatenbank übernommen.',
  });
}

export function useDismissCandidate() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id }) =>
      pulseFetch(`/v1/errors/admin/candidates/${id}/dismiss`, {
        method: 'POST',
        headers: authHeader(auth.getAccessToken()),
      }),
    fixture: () => ({ ok: true }),
    invalidate: ['kb-candidates'],
    successMessage: () => 'Kandidat verworfen.',
  });
}
